import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { SelectModule } from 'primeng/select';
import { CopilotContextComposerService } from '@brighthub/copilot';
import {
  CHAT_FILTER_CHIPS,
  ChatFilter,
  RenderedChatEvent,
  VIEW_MODE_OPTIONS,
  ViewMode,
} from '../client-chat-thread.model';
import { ClientChatThreadStateService } from '../client-chat-thread-state.service';
import { FaInviteStateService } from '../fa-invite-state.service';
import { FaInviteModalComponent, InviteSentEvent } from '../fa-invite-modal/fa-invite-modal.component';

@Component({
  selector: 'bai-client-chat-thread',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, TooltipModule, SelectModule, FaInviteModalComponent],
  templateUrl: './client-chat-thread.component.html',
  styleUrl: './client-chat-thread.component.scss',
})
export class ClientChatThreadComponent {
  @ViewChild('threadScrollContainer') threadScrollContainerRef?: ElementRef<HTMLElement>;

  private readonly threadState = inject(ClientChatThreadStateService);
  private readonly copilotComposer = inject(CopilotContextComposerService);
  private readonly inviteState = inject(FaInviteStateService);

  readonly sessionId = input<string | null>(null);
  /** Real client full name to display instead of the generic "Client" label. */
  readonly clientName = input<string>('Client');
  /** Display name of the currently logged-in FA (used in invite notes and fallback labels). */
  readonly inviterName = input<string>('Financial Assistant');

  readonly showInviteModal = signal(false);

  readonly filterChips = CHAT_FILTER_CHIPS;
  readonly viewModeOptions = VIEW_MODE_OPTIONS;

  readonly events = computed<RenderedChatEvent[]>(() => {
    const id = this.sessionId();
    if (!id) return [];
    return this.threadState.renderedFilteredEvents(id);
  });

  readonly activeFilter = computed<ChatFilter>(() => {
    const id = this.sessionId();
    if (!id) return 'all';
    return this.threadState.getState(id).activeFilter;
  });

  readonly viewMode = computed<ViewMode>(() => {
    const id = this.sessionId();
    if (!id) return 'full';
    return this.threadState.getState(id).viewMode;
  });

  readonly isLoading = computed<boolean>(() => {
    const id = this.sessionId();
    if (!id) return false;
    return this.threadState.getState(id).loading;
  });

  readonly isEmpty = computed<boolean>(() => !this.isLoading() && this.events().length === 0);

  /**
   * Groups consecutive events from the same speaker so we only show the
   * speaker header once per run of messages.
   */
  readonly groupedEvents = computed<Array<{ event: RenderedChatEvent; showHeader: boolean }>>(() => {
    const evts = this.events();
    return evts.map((event, idx) => {
      const prev = evts[idx - 1];
      const showHeader =
        !prev ||
        prev.speakerId !== event.speakerId ||
        prev.eventType !== event.eventType ||
        event.eventType === 'system_handoff_event' ||
        event.eventType === 'conversation_note';
      return { event, showHeader };
    });
  });

  constructor() {
    // Scroll to bottom after the initial paint so the ViewChild ref is resolved.
    afterNextRender(() => setTimeout(() => this.scrollToBottom('auto'), 100));

    // Auto-scroll on every thread update so the latest message is always visible.
    effect(() => {
      const evts = this.events();
      if (evts.length === 0) return;
      setTimeout(() => {
        this.scrollToBottom();
      }, 80);
    });
  }

  setFilter(filter: ChatFilter): void {
    const id = this.sessionId();
    if (!id) return;
    this.threadState.setFilter(id, filter);
  }

  setViewMode(mode: ViewMode): void {
    const id = this.sessionId();
    if (!id) return;
    this.threadState.setViewMode(id, mode);
  }

  scrollToBottom(behavior: ScrollBehavior = 'smooth'): void {
    const el = this.threadScrollContainerRef?.nativeElement;
    if (el) {
      if (typeof el.scrollTo === 'function') {
        el.scrollTo({ top: el.scrollHeight, behavior });
      } else {
        el.scrollTop = el.scrollHeight;
      }
    }
  }

  /**
   * Returns the display name for a client_message event.
   * Uses the real client name passed via input, falling back to the event's
   * speakerDisplayName or the generic "Client" label.
   */
  clientDisplayName(event: RenderedChatEvent): string {
    if (event.eventType === 'client_message') {
      return this.clientName() || event.speakerDisplayName || 'Client';
    }
    return event.speakerDisplayName || '';
  }

  /**
   * Returns display name for assistant_message_human rows.
   * Falls back to active inviter name when payload omits speakerDisplayName.
   */
  humanDisplayName(event: RenderedChatEvent): string {
    return event.speakerDisplayName || this.inviterName() || 'Financial Assistant';
  }

  /** Returns initials from a display name, e.g. "Jordan Lee" → "JL". */
  getInitials(name: string | undefined): string {
    if (!name) return '?';
    return name
      .split(' ')
      .map((w) => w[0] ?? '')
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  formatTime(isoString: string): string {
    try {
      return new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      }).format(new Date(isoString));
    } catch {
      return '';
    }
  }

  openInviteModal(): void {
    this.showInviteModal.set(true);
  }

  closeInviteModal(): void {
    this.showInviteModal.set(false);
  }

  onInviteSent(event: InviteSentEvent): void {
    const id = this.sessionId();
    if (!id) return;
    this.showInviteModal.set(false);
    const noteEvent = this.inviteState.sendInvite(id, event.faId, event.reason, this.inviterName());
    this.threadState.appendLocalEvent(id, noteEvent);
  }

  onInviteAccepted(faId: string): void {
    const id = this.sessionId();
    if (!id) return;
    const joinEvent = this.inviteState.acceptInvite(id, faId);
    if (joinEvent) {
      this.threadState.appendLocalEvent(id, joinEvent);
    }
  }

  addEventToCopilot(event: RenderedChatEvent): void {
    const speaker = this._eventSpeakerLabel(event);
    const preview = this._truncate(event.content, 20);
    const label = `${speaker}: ${preview}`;
    const ts = event.createdAt;
    const fullText = `[Client Chat | ${speaker} | ${ts}] ${event.content}`;

    this.copilotComposer.addContext({
      sourceType: 'client-chat',
      sourceId: event.id,
      label,
      fullText,
    });
  }

  private _eventSpeakerLabel(event: RenderedChatEvent): string {
    switch (event.eventType) {
      case 'client_message':
        return this.clientDisplayName(event);
      case 'assistant_message_human':
        return this.humanDisplayName(event);
      case 'assistant_message_ai':
        return event.speakerDisplayName || 'AIFA';
      case 'conversation_note':
        return 'Internal Note';
      case 'system_handoff_event':
        return 'Handoff';
      default:
        return 'Message';
    }
  }

  private _truncate(value: string, max = 20): string {
    if (value.length <= max) return value;
    return `${value.slice(0, max)}…`;
  }
}
